import React, { useState, useEffect, useContext } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import GameHistoryTable from './GameHistoryTable';
import { AuthContext } from '../App';
import { useTheme } from './ThemeContext';
import axios from 'axios';

const UserProfile = () => {
  const { woodStyles } = useTheme();
  const { user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [gameType, setGameType] = useState('all');

  // Colors for the pie chart
  const COLORS = ['#10B981', '#EF4444', '#F59E0B'];

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        // Fetch user stats with game type filter
        const statsResponse = await axios.get(`/api/users/${user.id}/stats?gameType=${gameType}`);
        setStats(statsResponse.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user.id, gameType]);

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Loading skeleton for User Info Header */}
        <div
          className={`rounded-lg border-2 shadow-xl overflow-hidden 
            ${woodStyles.card.background} 
            ${woodStyles.card.border}`}
        >
          <div
            className={`px-6 py-4 
              ${woodStyles.background}`}
            style={{
              backgroundImage: woodStyles.backgroundImage
            }}
          >
            <div className="h-8 w-48 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Loading skeleton for Stats Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart Card Skeleton */}
          <div
            className={`rounded-lg border-2 shadow-xl overflow-hidden 
              ${woodStyles.card.background} 
              ${woodStyles.card.border}`}
          >
            <div
              className={`px-4 py-2 border-b 
                ${woodStyles.card.border}`}
            >
              <div className="h-6 w-36 bg-gray-300 rounded animate-pulse"></div>
            </div>
            <div className="p-4">
              <div className="h-64 w-full bg-gray-300 rounded animate-pulse"></div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`text-center p-3 rounded-lg 
                      ${woodStyles.surface.secondary}`}
                  >
                    <div className="h-6 w-16 mx-auto bg-gray-300 rounded animate-pulse mb-2"></div>
                    <div className="h-4 w-20 mx-auto bg-gray-300 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Stats Card Skeleton */}
          <div
            className={`rounded-lg border-2 shadow-xl overflow-hidden 
              ${woodStyles.card.background} 
              ${woodStyles.card.border}`}
          >
            <div
              className={`px-4 py-2 border-b 
                ${woodStyles.card.border}`}
            >
              <div className="h-6 w-36 bg-gray-300 rounded animate-pulse"></div>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex justify-between">
                    <div className="h-4 w-24 bg-gray-300 rounded animate-pulse"></div>
                    <div className="h-4 w-12 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Game History Table Skeleton handled by GameHistoryTable component */}
        <GameHistoryTable userId={user.id} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-4">
        <p className={`text-red-500 ${woodStyles.text.secondary}`}>{error}</p>
      </div>
    );
  }

  // Prepare data for pie chart
  const chartData = [
    { name: 'Wins', value: stats?.wins || 0 },
    { name: 'Losses', value: stats?.losses || 0 },
    { name: 'Draws', value: stats?.draws || 0 }
  ];

  const totalGames = stats?.totalGames || 0;
  const winRate = totalGames > 0 ? ((stats?.wins / totalGames) * 100).toFixed(1) : 0;
  const lossRate = totalGames > 0 ? ((stats?.losses / totalGames) * 100).toFixed(1) : 0;
  const drawRate = totalGames > 0 ? ((stats?.draws / totalGames) * 100).toFixed(1) : 0;

  return (
    <div
      className="max-w-6xl mx-auto p-4 space-y-6"
      style={{
        background: woodStyles.background,
        backgroundImage: woodStyles.backgroundImage
      }}
    >
      {/* User Info Header */}
      <div
        className={`rounded-lg border-2 shadow-xl overflow-hidden 
          ${woodStyles.card.background} 
          ${woodStyles.card.border}`}
      >
        <div
          className={`px-6 py-4 
            ${woodStyles.background}`}
          style={{
            backgroundImage: woodStyles.backgroundImage
          }}
        >
          <div className="flex justify-between items-center">
            <h1 className={`text-xl font-bold ${woodStyles.text.primary}`}>
              Profile: {user.username || user.id}
            </h1>
            <select
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
              className={`px-3 py-1.5 rounded text-sm
                ${woodStyles.inlay.background}
                ${woodStyles.text.primary}
                ${woodStyles.card.border}
                border`}
            >
              <option value="all">All Games</option>
              <option value="anarchy">Anarchy Games</option>
              <option value="normal">Normal Games</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart Card */}
        <div
          className={`rounded-lg border-2 shadow-xl overflow-hidden 
            ${woodStyles.card.background} 
            ${woodStyles.card.border}`}
        >
          <div
            className={`px-4 py-2 border-b 
              ${woodStyles.card.border}`}
          >
            <h2 className={`font-semibold ${woodStyles.text.primary}`}>
              Performance Overview
            </h2>
          </div>
          <div className="p-4">
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} (${((value / totalGames) * 100).toFixed(1)}%)`, name]}
                    contentStyle={{
                      backgroundColor: woodStyles.surface.secondary,
                      borderColor: woodStyles.card.border,
                      borderRadius: '0.375rem',
                    }}
                    labelStyle={{
                      color: woodStyles.text.primary,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className={`text-center p-3 rounded-lg ${woodStyles.surface.secondary}`}>
                <div className="text-emerald-500 font-bold text-lg">{winRate}%</div>
                <div className={`text-sm ${woodStyles.text.secondary}`}>Win Rate</div>
                <div className={`text-xs ${woodStyles.text.accent}`}>
                  {stats?.wins} games
                </div>
              </div>
              <div className={`text-center p-3 rounded-lg ${woodStyles.surface.secondary}`}>
                <div className="text-red-500 font-bold text-lg">{lossRate}%</div>
                <div className={`text-sm ${woodStyles.text.secondary}`}>Loss Rate</div>
                <div className={`text-xs ${woodStyles.text.accent}`}>
                  {stats?.losses} games
                </div>
              </div>
              <div className={`text-center p-3 rounded-lg ${woodStyles.surface.secondary}`}>
                <div className="text-yellow-500 font-bold text-lg">{drawRate}%</div>
                <div className={`text-sm ${woodStyles.text.secondary}`}>Draw Rate</div>
                <div className={`text-xs ${woodStyles.text.accent}`}>
                  {stats?.draws} games
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats Card */}
        <div
          className={`rounded-lg border-2 shadow-xl overflow-hidden 
            ${woodStyles.card.background} 
            ${woodStyles.card.border}`}
        >
          <div
            className={`px-4 py-2 border-b 
              ${woodStyles.card.border}`}
          >
            <h2 className={`font-semibold ${woodStyles.text.primary}`}>
              Game Statistics
            </h2>
          </div>
          <div className="p-4 space-y-4">
            <div className={`grid grid-cols-2 gap-4 ${woodStyles.text.primary}`}>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Games:</span>
                  <span className="font-semibold">{totalGames}</span>
                </div>
                <div className="flex justify-between">
                  <span>Average Time Control:</span>
                  <span className="font-semibold">{stats?.averageTimeControl?.toFixed(1) || 0}s</span>
                </div>
                <div className="flex justify-between">
                  <span>Game Mode:</span>
                  <span className="font-semibold capitalize">{gameType}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Games as White:</span>
                  <span className="font-semibold">{stats?.gamesAsWhite || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Games as Black:</span>
                  <span className="font-semibold">{stats?.gamesAsBlack || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Average Time/Move:</span>
                  <span className="font-semibold">
                    {stats?.averageTimePerMove?.toFixed(1) || 0}s
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Game History Table */}
      <GameHistoryTable userId={user.id} gameType={gameType} />
    </div>
  );
};

export default UserProfile;